/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { AtomicFacet, AtomicNumericFacet } from "@coveo/atomic-react";
import { useEffect, useRef, useState } from "react";

interface CoveoSearchFacetProps {
  content: SearchFacetFields;
}

export interface SearchFacetFieldsData {
  metadata: { tags: string[] };
  fields: SearchFacetFields;
}

interface SearchFacetFields {
  label: string;
  coveoFieldName: string;
  coveoFacetType: string;
}

const SORT_MAP = {
  articledateyear: "alphanumericDescending",
  dponationality: "alphanumeric",
};

export const CoveoSearchFacet = ({ content }: CoveoSearchFacetProps) => {
  const { label, coveoFieldName, coveoFacetType } = content;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const facetWrapperRef = useRef<HTMLDivElement>(null);
  const facetRef = useRef(null);

  // Toggle collapse state
  const closeFacet = () => setIsCollapsed(true);

  useEffect(() => {
    if (!isCollapsed) {
      // Handle clicking outside the facet wrapper
      const handleClickOutside = (event: MouseEvent) => {
        if (
          facetWrapperRef.current &&
          !facetWrapperRef.current.contains(event.target as Node)
        ) {
          closeFacet();
        }
      };

      // Handle focusing outside the facet wrapper
      const handleFocusOutside = (event: FocusEvent) => {
        if (
          facetWrapperRef.current &&
          event.target instanceof Node &&
          !facetWrapperRef.current.contains(event.target)
        ) {
          closeFacet();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("focusin", handleFocusOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("focusin", handleFocusOutside);
      };
    }
  }, [isCollapsed]);

  // Keyboard support
  useEffect(() => {
    const facetEl = facetRef.current;
    if (!facetEl) return;

    // Track keys handled on keydown to prevent double handling
    const handledKeys = new Set<string>();

    const onKeyDown = (event: KeyboardEvent) => {
      const shadowRoot = facetEl.shadowRoot;
      if (!shadowRoot) return;

      // Relevant keys to handle
      const relevantKeys = new Set(["ArrowDown", "ArrowUp", "Escape"]);
      if (!relevantKeys.has(event.key)) return;

      const options: HTMLButtonElement[] = Array.from(
        shadowRoot.querySelectorAll(`[part~="value-checkbox"]`)
      );
      const labelButton: HTMLButtonElement = shadowRoot.querySelector(
        `[part="label-button"]`
      );

      switch (event.key) {
        case "ArrowDown":
        case "ArrowUp": {
          event.preventDefault();
          if (options.length === 0) return;

          // Find currently focused option inside shadowRoot
          const activeEl = shadowRoot.activeElement as HTMLElement | null;
          let focusedIndex = options.findIndex((opt) => opt === activeEl);

          if (focusedIndex === -1) {
            // Not focused inside options, focus first or last based on key
            focusedIndex = event.key === "ArrowDown" ? 0 : options.length - 1;
          } else {
            // Move focus cyclically
            focusedIndex =
              event.key === "ArrowDown"
                ? (focusedIndex + 1) % options.length
                : (focusedIndex - 1 + options.length) % options.length;
          }
          options[focusedIndex].focus();
          break;
        }

        case "Escape": {
          event.preventDefault();
          if (!isCollapsed && labelButton) {
            // Focus toggle button after close
            labelButton.focus();
            closeFacet();
          }
          break;
        }
      }

      // Mark this key as handled on keydown
      handledKeys.add(event.key);
    };

    const onKeyUp = (event: KeyboardEvent) => {
      const shadowRoot = facetEl.shadowRoot;
      if (!shadowRoot) return;

      const relevantKeys = new Set(["ArrowDown", "ArrowUp"]);
      if (!relevantKeys.has(event.key)) return;

      // If key was already handled on keydown, skip here
      if (handledKeys.has(event.key)) {
        // Clean up the flag on keyup
        handledKeys.delete(event.key);
        return;
      }

      const options: HTMLButtonElement[] = Array.from(
        shadowRoot.querySelectorAll(`[part~="value-checkbox"]`)
      );

      switch (event.key) {
        case "ArrowDown":
        case "ArrowUp": {
          event.preventDefault();
          if (options.length === 0) return;

          // Find currently focused option inside shadowRoot
          const activeEl = shadowRoot.activeElement as HTMLElement | null;
          let focusedIndex = options.findIndex((opt) => opt === activeEl);

          if (focusedIndex === -1) {
            // Not focused inside options, focus first or last based on key
            focusedIndex = event.key === "ArrowDown" ? 0 : options.length - 1;
          } else {
            // Move focus cyclically
            focusedIndex =
              event.key === "ArrowDown"
                ? (focusedIndex + 1) % options.length
                : (focusedIndex - 1 + options.length) % options.length;
          }

          options[focusedIndex].focus();
          break;
        }
      }
    };

    facetEl.addEventListener("keydown", onKeyDown);
    facetEl.addEventListener("keyup", onKeyUp);
    return () => {
      facetEl.removeEventListener("keydown", onKeyDown);
      facetEl.removeEventListener("keyup", onKeyUp);
    };
  }, [isCollapsed]);

  let facet = null;

  const facetSort = SORT_MAP?.[coveoFieldName] || "occurrences";

  if (coveoFacetType === "atomic-facet") {
    facet = (
      <AtomicFacet
        facet-id={coveoFieldName}
        ref={facetRef}
        withSearch={false}
        isCollapsed={isCollapsed}
        field={coveoFieldName}
        label={label}
        numberOfValues={500}
        sortCriteria={facetSort}
      />
    );
  } else if (coveoFacetType === "atomic-numeric-facet") {
    facet = <AtomicNumericFacet field={coveoFieldName} label={label} />;
  } else {
    return null;
  }

  return (
    <div ref={facetWrapperRef} onClick={() => setIsCollapsed(false)}>
      {facet}
    </div>
  );
};

export default CoveoSearchFacet;
